import { motion } from "framer-motion";
import { IndianRupee, TrendingUp, Wallet, CreditCard } from "lucide-react";

const steps = [
  {
    icon: CreditCard,
    step: "01",
    title: "Create & Price Your Exam",
    description: "Set a custom credit cost for each exam you create. You decide how much students pay to unlock it.",
  },
  {
    icon: TrendingUp,
    step: "02",
    title: "Students Unlock & Attempt",
    description: "Students purchase credits and use them to unlock your exams. Every unlock earns you credits automatically.",
  },
  {
    icon: Wallet,
    step: "03",
    title: "Earn Real Money",
    description: "Your earned credits convert at 2 credits = ₹1. Withdraw via Bank Transfer, UPI, or PayPal once you hit 500 credits.",
  },
];

const TeacherEarnings = () => {
  return (
    <section className="py-24 px-6 bg-muted/30">
      <div className="container mx-auto">
        {/* Header */}
        <div className="grid lg:grid-cols-2 gap-12 items-end mb-20">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <div className="inline-flex items-center gap-2 bg-primary/10 text-primary rounded-full px-4 py-1.5 text-sm font-semibold mb-4">
              <IndianRupee className="h-4 w-4" />
              For Teachers
            </div>
            <h2 className="text-4xl md:text-5xl font-black text-foreground leading-[1.05] tracking-tight">
              Teach, create exams &{" "}
              <span className="text-primary">earn money.</span>
            </h2>
          </motion.div>
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.1 }}
            className="text-lg text-muted-foreground leading-relaxed"
          >
            Every time a student unlocks your exam, you earn credits that convert to real money. No upfront cost — just create, share, and get paid.
          </motion.p>
        </div>

        {/* Steps */}
        <div className="grid md:grid-cols-3 gap-8">
          {steps.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: index * 0.15 }}
              className="relative bg-card border rounded-3xl p-8 hover:shadow-xl hover:-translate-y-1 transition-all duration-300 group"
            >
              <span className="text-6xl font-black text-primary/10 group-hover:text-primary/20 transition-colors absolute top-6 right-6">
                {item.step}
              </span>
              <div className="h-14 w-14 rounded-2xl bg-primary/20 flex items-center justify-center mb-6 group-hover:bg-primary group-hover:scale-110 transition-all duration-300">
                <item.icon className="h-7 w-7 text-foreground group-hover:text-primary-foreground transition-colors" />
              </div>
              <h3 className="text-xl font-bold text-foreground mb-3">{item.title}</h3>
              <p className="text-muted-foreground text-sm leading-relaxed">{item.description}</p>
            </motion.div>
          ))}
        </div>

        {/* Conversion highlight */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.3 }}
          className="mt-12 bg-card border-2 border-primary/20 rounded-2xl p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-6"
        >
          <div className="flex items-center gap-4">
            <div className="h-12 w-12 rounded-xl bg-primary/15 flex items-center justify-center shrink-0">
              <IndianRupee className="h-6 w-6 text-primary" />
            </div>
            <div>
              <p className="font-bold text-foreground text-lg">2 Credits = ₹1</p>
              <p className="text-sm text-muted-foreground">Minimum payout: 500 credits (₹250) • Bank, UPI & PayPal supported</p>
            </div>
          </div>
          <div className="text-center md:text-right">
            <p className="text-3xl font-black text-primary">₹0</p>
            <p className="text-xs text-muted-foreground font-medium">Platform fee for teachers</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default TeacherEarnings;
